<template>
  <div class="bank-card">
    <div class="bank-card__head">
      <img
        :src="logo"
        alt="Santander"
        class="bank-card__logo"
      >
      <div class="bank-card__beneficiary">
        <span class="bank-card__label">Transfiere a</span>
        <strong>{{ payment.name }}</strong>
      </div>
    </div>

    <ul class="bank-card__fields">
      <li
        v-for="field in fields"
        :key="field.key"
        class="bank-field"
      >
        <div class="bank-field__text">
          <span class="bank-card__label">{{ field.label }}</span>
          <span class="bank-field__value">{{ field.value }}</span>
        </div>
        <b-button
          size="sm"
          variant="flat-primary"
          class="bank-field__copy"
          :title="`Copiar ${field.label.toLowerCase()}`"
          @click="copy(field)"
        >
          <feather-icon
            :icon="copied === field.key ? 'CheckIcon' : 'CopyIcon'"
            size="15"
          />
          <span class="bank-field__copy-text">
            {{ copied === field.key ? 'Copiado' : 'Copiar' }}
          </span>
        </b-button>
      </li>
    </ul>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import santanderLogo from '@/assets/images/banks/santander.svg'

export default {
  components: {
    BButton,
  },

  props: {
    payment: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      logo: santanderLogo,
      copied: null,
      copiedTimer: null,
    }
  },

  computed: {
    fields() {
      return [
        { key: 'clabe', label: 'CLABE interbancaria', value: this.payment.clabe },
        { key: 'accountNumber', label: 'Número de cuenta', value: this.payment.accountNumber },
        { key: 'card', label: 'Tarjeta', value: this.payment.card },
      ].filter(field => field.value)
    },
  },

  beforeDestroy() {
    clearTimeout(this.copiedTimer)
  },

  methods: {
    async copy(field) {
      const text = String(field.value).replace(/\s/g, '')

      try {
        await this.writeToClipboard(text)
        this.copied = field.key
        clearTimeout(this.copiedTimer)
        this.copiedTimer = setTimeout(() => { this.copied = null }, 2000)
      } catch (error) {
        this.$toast.error('No se pudo copiar. Selecciona el dato y cópialo a mano.')
      }
    },

    // El portapapeles moderno necesita contexto seguro; si el navegador lo niega se cae al
    // metodo viejo antes de darse por vencido.
    writeToClipboard(text) {
      if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text)

      return new Promise((resolve, reject) => {
        const helper = document.createElement('textarea')
        helper.value = text
        helper.setAttribute('readonly', '')
        helper.style.position = 'absolute'
        helper.style.left = '-9999px'
        document.body.appendChild(helper)
        helper.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(helper)

        return ok ? resolve() : reject(new Error('copy failed'))
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.bank-card {
  border: 1px solid #ebe9f1;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #ffffff;
}

.bank-card__head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #ebe9f1;
  background: #fbfaff;
}

// El logotipo es apaisado (5.3:1), asi que manda el ancho y no el alto: con max-width se
// achica solo en columnas angostas en vez de desbordar la tarjeta.
.bank-card__logo {
  width: 100%;
  max-width: 9.5rem;
  height: auto;
}

.bank-card__beneficiary {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.25;

  strong {
    font-size: 0.82rem;
    color: #25233a;
    word-break: break-word;
  }
}

.bank-card__label {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #b2aebf;
}

.bank-card__fields {
  list-style: none;
  margin: 0;
  padding: 0;
}

.bank-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.65rem 1rem;

  & + & {
    border-top: 1px solid #f4f3f7;
  }
}

.bank-field__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.bank-field__value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #25233a;
  font-variant-numeric: tabular-nums;
  word-break: break-all;
}

.bank-field__copy {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.35rem 0.55rem;
}

.bank-field__copy-text {
  font-size: 0.72rem;
  font-weight: 600;
}
</style>
